/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 批量编辑水印测试页面
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-01-08 11:18:16
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Layout,
  Button,
  Table,
  message,
  Modal,
  Spin,
  Tag,
  Tooltip,
  Drawer,
  Upload
} from 'antd'
import type { UploadProps } from 'antd/es/upload/interface'
import {
  CheckCircleOutlined,
  DownloadOutlined,
  DropboxOutlined,
  ExclamationCircleOutlined,
  InboxOutlined,
  LeftOutlined,
  LoadingOutlined,
  RightOutlined,
  ThunderboltOutlined
} from '@ant-design/icons'
import { useCSVUpload, TestDataItem } from './hooks/useCSVUpload'
import { WatermarkExtractionStatus } from '../context/WatermarkBatchContext'
import { useUserId } from '../../../../app/hooks'
import PreviewDrawerContent from './components/PreviewDrawerContent'
import { useUploadDataToOSS } from './hooks/useUploadDataToOSS'
import { useWatermarkExtract } from './hooks/useWatermarkExtract'
import { useWatermarkApplyLogic } from './hooks/useWatermarkApplyLogic'
import { useProcessedData } from './hooks/useProcessedData'
import { createTableColumns } from './components/TableColumns'
import { parseDiffData, getProcessStatusInfo } from './utils/watermarkUtils'
import BatchAutoProcessModal from './components/BatchAutoProcessModal'

import PageHeader from '@/components/PageHeader'
import AntdProvider from '@/components/AntdProvider'
import './index.scss'

const { Dragger } = Upload

const { Content } = Layout

// 声明 Vite define 定义的全局变量
declare const __BUILD_ENV__: string

const WatermarkBatchEditorTest: React.FC = () => {
  const { dataSource, loading, handleFileUpload, updateDataSource } =
    useCSVUpload()
  const [uploadModalVisible, setUploadModalVisible] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [extracting, setExtracting] = useState(false)
  const [applying, setApplying] = useState(false)
  const [hoveredRecord, setHoveredRecord] = useState<TestDataItem | null>(null)
  const [showBatchAutoProcessModal, setShowBatchAutoProcessModal] =
    useState(false)
  const userId = useUserId()
  const { readKeyList } = useUploadDataToOSS()

  // 内存中持有的key列表（已存在的处理数据的key）
  const [processedDataKeysSet, setProcessedDataKeysSet] = useState<Set<string>>(
    new Set()
  )

  // 当前页是否有提取完成的数据（本地状态）
  const [hasCompletedExtraction, setHasCompletedExtraction] = useState(false)

  // 当前页是否有应用成功的数据（本地状态）
  const [hasCompletedApply, setHasCompletedApply] = useState(false)

  // 检查当前页状态的辅助函数（仅用于检查提取状态，应用状态通过操作成功来设置）
  const checkCurrentPageStatus = useCallback(() => {
    if (dataSource.length === 0) {
      setHasCompletedExtraction(false)
      return
    }

    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    const currentPageData = dataSource.slice(startIndex, endIndex)

    // 检查是否有提取完成的数据
    const hasExtraction = currentPageData.some(
      (item) =>
        item.extractionStatus === WatermarkExtractionStatus.COMPLETED &&
        item.watermarkData &&
        item.watermarkData.length > 0 &&
        item.diff
    )
    setHasCompletedExtraction(hasExtraction)
  }, [dataSource, currentPage, pageSize])

  // 页面切换时重置应用状态（因为应用状态是基于操作的，切换页面后需要重新操作）
  useEffect(() => {
    setHasCompletedApply(false)
  }, [currentPage, pageSize])

  // 当数据源、当前页或页面大小变化时，更新状态
  useEffect(() => {
    checkCurrentPageStatus()
  }, [checkCurrentPageStatus])

  // 页面加载时显示上传弹窗
  useEffect(() => {
    setUploadModalVisible(true)
  }, [])

  // 监听 loading 状态变化，上传成功后隐藏弹窗并读取key列表
  useEffect(() => {
    if (!loading && dataSource.length > 0) {
      setUploadModalVisible(false)

      // CSV上传完成后，读取key列表
      const loadKeyList = async () => {
        try {
          const keys = await readKeyList()
          if (keys && keys.length > 0) {
            setProcessedDataKeysSet(new Set(keys))
            console.log('数据key列表加载成功，共', keys.length, '个key')
          } else {
            console.log('数据key列表为空或不存在')
            setProcessedDataKeysSet(new Set())
          }
        } catch (error) {
          console.error('读取数据key列表失败:', error)
          setProcessedDataKeysSet(new Set())
        }
      }

      loadKeyList()
    }
  }, [loading, dataSource.length, readKeyList])

  // 获取当前页的数据范围
  const getCurrentPageData = useCallback(() => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    return dataSource.slice(startIndex, endIndex)
  }, [dataSource, currentPage, pageSize])

  // 只获取当前页的数据用于表格渲染（优化性能，只加载当前页图片）
  const currentPageDataSource = useMemo(() => {
    return getCurrentPageData()
  }, [getCurrentPageData])

  // 使用数据处理 Hook
  const { handleSaveRemark } = useProcessedData({
    dataSource,
    currentPage,
    pageSize,
    processedDataKeysSet,
    setProcessedDataKeysSet,
    updateDataSource,
    checkCurrentPageStatus,
    getCurrentPageData
  })

  // 导航到上一张（限制在当前页内）
  const handlePrevRecord = useCallback(() => {
    if (!hoveredRecord || dataSource.length === 0) return

    const currentPageData = getCurrentPageData()
    const currentIndexInPage = currentPageData.findIndex(
      (item) => item.key === hoveredRecord.key
    )

    if (currentIndexInPage > 0) {
      setHoveredRecord(currentPageData[currentIndexInPage - 1])
    } else {
      message.info('已经是当前页的第一张了')
    }
  }, [hoveredRecord, dataSource, getCurrentPageData])

  // 导航到下一张（限制在当前页内）
  const handleNextRecord = useCallback(() => {
    if (!hoveredRecord || dataSource.length === 0) return

    const currentPageData = getCurrentPageData()
    const currentIndexInPage = currentPageData.findIndex(
      (item) => item.key === hoveredRecord.key
    )

    if (currentIndexInPage < currentPageData.length - 1) {
      setHoveredRecord(currentPageData[currentIndexInPage + 1])
    } else {
      message.info('已经是当前页的最后一张了')
    }
  }, [hoveredRecord, dataSource, getCurrentPageData])

  // 检查 hoveredRecord 是否在当前页，如果不在则关闭并提示
  useEffect(() => {
    if (!hoveredRecord) return

    const currentPageData = getCurrentPageData()
    const isInCurrentPage = currentPageData.some(
      (item) => item.key === hoveredRecord.key
    )

    if (!isInCurrentPage) {
      message.warning('该图片不在当前页，预览面板只能查看当前页的图片')
      setHoveredRecord(null)
    }
  }, [hoveredRecord, currentPage, pageSize, getCurrentPageData])

  // 使用提取逻辑 Hook
  const { handleExtract } = useWatermarkExtract({
    dataSource,
    currentPage,
    pageSize,
    updateDataSource,
    setExtracting,
    setHasCompletedExtraction
  })

  // 使用应用逻辑 Hook
  const { handleApply, handleReapplySingle } = useWatermarkApplyLogic({
    dataSource,
    currentPage,
    pageSize,
    userId,
    updateDataSource,
    setApplying,
    setHasCompletedApply,
    setProcessedDataKeysSet
  })

  // 键盘事件处理（左右箭头键）
  useEffect(() => {
    if (!hoveredRecord) return

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        handlePrevRecord()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        handleNextRecord()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [hoveredRecord, handlePrevRecord, handleNextRecord])

  // 上传测试数据 - 用于 Dragger
  const uploadProps: UploadProps = {
    name: 'file',
    accept: '.csv',
    showUploadList: false,
    beforeUpload: (file) => {
      handleFileUpload(file)
      return false
    }
  }

  // 生成表格列
  const columns = useMemo(
    () =>
      createTableColumns({
        currentPage,
        pageSize,
        setHoveredRecord,
        handleReapplySingle,
        updateDataSource
      }),
    [currentPage, pageSize, handleReapplySingle, updateDataSource]
  )
  // 非生产环境不显示任何内容
  if (__BUILD_ENV__ === 'prod') {
    return null
  }

  return (
    <Layout className="watermark-batch-editor-test">
      <PageHeader
        ghost={false}
        title="批量编辑水印测试"
        extra={
          <div className="page-header-extra">
            <div className="steps-container">
              <div
                className={`step-item ${extracting ? 'processing' : ''} ${
                  hasCompletedExtraction ? 'completed' : ''
                }`}
                onClick={handleExtract}
                style={{
                  cursor:
                    extracting || applying || dataSource.length === 0
                      ? 'not-allowed'
                      : 'pointer',
                  opacity:
                    extracting || applying || dataSource.length === 0 ? 0.5 : 1
                }}
              >
                <DownloadOutlined className="step-icon" />
                <span className="step-text">第一步: 提取</span>
                {extracting && <LoadingOutlined style={{ marginLeft: 8 }} />}
                {hasCompletedExtraction && !extracting && (
                  <CheckCircleOutlined
                    style={{ marginLeft: 8, color: '#52c41a' }}
                  />
                )}
              </div>
              <div className="step-divider"></div>
              <div
                className={`step-item ${applying ? 'processing' : ''} ${
                  hasCompletedApply ? 'completed' : ''
                }`}
                onClick={handleApply}
                style={{
                  cursor:
                    extracting ||
                    applying ||
                    dataSource.length === 0 ||
                    !hasCompletedExtraction
                      ? 'not-allowed'
                      : 'pointer',
                  opacity:
                    extracting ||
                    applying ||
                    dataSource.length === 0 ||
                    !hasCompletedExtraction
                      ? 0.5
                      : 1
                }}
              >
                <DropboxOutlined className="step-icon" />
                <span className="step-text">第二步: 应用水印</span>
                {applying && <LoadingOutlined style={{ marginLeft: 8 }} />}
                {!hasCompletedExtraction && (
                  <ExclamationCircleOutlined
                    style={{ marginLeft: 8, color: '#999999' }}
                  />
                )}
                {hasCompletedApply && !applying && (
                  <CheckCircleOutlined
                    style={{ marginLeft: 8, color: '#52c41a' }}
                  />
                )}
              </div>
            </div>
            <Button
              type="primary"
              onClick={() => setShowBatchAutoProcessModal(true)}
              className="batch-process-btn"
            >
              <ThunderboltOutlined style={{ marginRight: 8 }} />
              批处理
            </Button>
          </div>
        }
      />
      <Content className="content">
        <div className="table-container">
          <Table
            dataSource={currentPageDataSource}
            columns={columns}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: dataSource.length,
              showSizeChanger: true,
              pageSizeOptions: ['50', '100', '200', '500'],
              showTotal: (total) => `共 ${total} 条数据`,
              onChange: (page, size) => {
                setCurrentPage(page)
                if (size && size !== pageSize) {
                  setPageSize(size)
                }
              }
            }}
            scroll={{
              x: 'max-content',
              y: window.innerHeight - 200
            }}
          />
        </div>
      </Content>

      {/* 大图预览 - Drawer 抽屉 */}
      {hoveredRecord && (
        <Drawer
          title={
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%'
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
              >
                <span>
                  图片预览
                  {hoveredRecord &&
                    ` (${
                      getCurrentPageData().findIndex(
                        (item) => item.key === hoveredRecord.key
                      ) + 1
                    } / ${getCurrentPageData().length})`}
                </span>
                {(() => {
                  const statusInfo = getProcessStatusInfo(hoveredRecord)
                  const statusTag = (
                    <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
                  )
                  const statusWithTooltip = statusInfo.errorMsg ? (
                    <Tooltip title={statusInfo.errorMsg} placement="top">
                      {statusTag}
                    </Tooltip>
                  ) : (
                    statusTag
                  )

                  return (
                    <>
                      {statusWithTooltip}
                      {hoveredRecord.applyTaskID && (
                        <span style={{ fontSize: '12px', color: '#666' }}>
                          TaskID: {hoveredRecord.applyTaskID}
                        </span>
                      )}
                    </>
                  )
                })()}
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  paddingRight: 50
                }}
              >
                <Button
                  onClick={handlePrevRecord}
                  disabled={
                    !hoveredRecord ||
                    getCurrentPageData().findIndex(
                      (item) => item.key === hoveredRecord.key
                    ) === 0
                  }
                >
                  <LeftOutlined />
                  上一张
                </Button>
                <Button
                  onClick={handleNextRecord}
                  disabled={
                    !hoveredRecord ||
                    getCurrentPageData().findIndex(
                      (item) => item.key === hoveredRecord.key
                    ) ===
                      getCurrentPageData().length - 1
                  }
                >
                  下一张
                  <RightOutlined />
                </Button>
              </div>
            </div>
          }
          placement="right"
          size="90%"
          open={!!hoveredRecord}
          onClose={() => {
            setHoveredRecord(null)
          }}
          closable={true}
          mask={{ closable: true }}
          style={{
            zIndex: 1000
          }}
          styles={{
            body: {
              padding: '16px',
              overflow: 'hidden',
              height: 'calc(100vh - 55px)'
            }
          }}
        >
          <PreviewDrawerContent
            record={hoveredRecord}
            parseDiffData={parseDiffData}
            onSaveRemark={handleSaveRemark}
          />
        </Drawer>
      )}

      {/* 上传弹窗 */}
      <Modal
        title="上传测试数据"
        open={uploadModalVisible}
        footer={null}
        closable={false}
        mask={{ closable: false }}
        width={600}
      >
        <Spin spinning={loading} tip="正在解析文件...">
          <Dragger {...uploadProps} disabled={loading}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ fontSize: 48, color: '#1890ff' }} />
            </p>
            <p className="ant-upload-text">
              点击选择文件或将文件拖拽至此处上传
            </p>
            <p className="ant-upload-hint">仅支持 CSV 格式文件</p>
          </Dragger>
        </Spin>
      </Modal>

      {/* 批量自动处理弹窗 */}
      <BatchAutoProcessModal
        visible={showBatchAutoProcessModal}
        onCancel={() => setShowBatchAutoProcessModal(false)}
        dataSource={dataSource}
        updateDataSource={updateDataSource}
      />
    </Layout>
  )
}

const WatermarkBatchEditorTestPage = () => (
  <AntdProvider>
    <WatermarkBatchEditorTest />
  </AntdProvider>
)

export default WatermarkBatchEditorTestPage
